import React from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Stack,
  Chip,
  Autocomplete,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";

interface Filter {
  id: string;
  type:
    | "status"
    | "priority"
    | "due_date"
    | "start_date"
    | "updated_date"
    | "project"
    | "recurring"
    | "tags"
    | "assignee"
    | "created_by";
  value: string | string[] | boolean;
  startDate?: string;
  endDate?: string;
}

interface FilterRowProps {
    filter: Filter;
    statusOptions: Array<{ value: string; label: string }>;
    priorityOptions: Array<{ value: string; label: string }>;
    projectOptions: Array<{ value: string; label: string }>;
    dueDateOptions: Array<{ value: string; label: string }>;
    startDateOptions: Array<{ value: string; label: string }>;
    updatedDateOptions: Array<{ value: string; label: string }>;
    tagOptions: Array<{ value: string; label: string }>;
    assigneeOptions: Array<{ value: string; label: string }>;
    createdByOptions: { value: string; label: string }[];
    onValueChange: (filterId: string, value: string | string[] | boolean) => void;
    onDateChange: (
      filterId: string,
      dateType: "start" | "end",
      date: string
    ) => void;
    onRemove: (filterId: string) => void;
  }

export const FilterRow: React.FC<FilterRowProps> = ({
  filter,
  statusOptions,
  priorityOptions,
  projectOptions,
  dueDateOptions,
  startDateOptions,
  updatedDateOptions,
  tagOptions,
  assigneeOptions,
  createdByOptions,
  onValueChange,
  onDateChange,
  onRemove,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  const getFilterTypeLabel = (type: Filter["type"]) => {
    const labels = {
      status: t("Status"),
      priority: t("Priority"),
      due_date: t("Due date"),
      start_date: t("Start date"),
      updated_date: t("Updated date"),
      project: t("Project"),
      recurring: t("Recurring"),
      tags: t("Tags"),
      assignee: t("Assignee"),
      created_by: t("Created by"),
    };
    return labels[type];
  };

  const getFilterOptions = (filterType: Filter["type"]) => {
    switch (filterType) {
      case "status":
        return statusOptions || [];
      case "priority":
        return priorityOptions || [];
      case "project":
        return projectOptions || [];
      case "tags":
        return tagOptions || [];
      case "assignee":
        return assigneeOptions || [];
      case "due_date":
        return dueDateOptions;
      case "start_date":
        return startDateOptions;
      case "updated_date":
        return updatedDateOptions;
      case "created_by":
        return createdByOptions;
      default:
        return [];
    }
  };

  const renderDateRangePicker = () => {
    if (filter.value !== "date_range") return null;

    return (
      <Box sx={{ mt: 2, pl: 13 }} onClick={(e) => e.stopPropagation()}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label={t("Start Date")}
            type="date"
            value={filter.startDate || ""}
            onChange={(e) => {
              e.stopPropagation();
              onDateChange(filter.id, "start", e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                fontSize: "14px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "14px",
              },
            }}
          />
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "14px",
              minWidth: "20px",
              textAlign: "center",
            }}
          >
            {t("to")}
          </Typography>
          <TextField
            label={t("End Date")}
            type="date"
            value={filter.endDate || ""}
            onChange={(e) => {
              e.stopPropagation();
              onDateChange(filter.id, "end", e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            inputProps={{
              min: filter.startDate || undefined,
            }}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                fontSize: "14px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "14px",
              },
            }}
          />
        </Stack>
      </Box>
    );
  };

  const renderRecurringSelector = () => {
    return (
      <FormControl sx={{ flex: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(filter.value)}
              onChange={(e) => {
                onValueChange(filter.id, e.target.checked);
              }}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                "&.Mui-checked": {
                  color: theme.palette.primary.main,
                },
              }}
            />
          }
          label={
            <Typography
              sx={{ fontSize: "13px", color: theme.palette.text.primary }}
            >
              {t("Recurring Tasks")}
            </Typography>
          }
          sx={{
            marginLeft: 0,
            "& .MuiFormControlLabel-label": {
              marginLeft: 1,
            },
          }}
        />
      </FormControl>
    );
  };

  const renderFilterSelector = () => {
    const options = getFilterOptions(filter.type);
  
    // Handle recurring filter with checkbox
    if (filter.type === "recurring") {
      return renderRecurringSelector();
    }
  
    // Handle multi-select filters (status, priority, project, tags, assignee)
    if (
      filter.type === "status" ||
      filter.type === "priority" ||
      filter.type === "project" ||
      filter.type === "tags" ||
      filter.type === "assignee" ||
      filter.type === "created_by"
    ) {
      return (
        <FormControl sx={{ flex: 1 }} size="small">
          <Autocomplete
            multiple
            limitTags={2}
            size="small"
            options={options}
            getOptionLabel={(option) => option.label}
            value={options.filter(
              (option) =>
                Array.isArray(filter.value) &&
                filter.value.includes(option.value)
            )}
            onChange={(event, newValue) => {
              const selectedValues = newValue.map((option) => option.value);
              onValueChange(filter.id, selectedValues);
            }}
            renderTags={(tagValues, getTagProps) =>
              tagValues.map((option, index) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  size="small"
                  sx={{
                    height: "20px",
                    fontSize: "11px",
                    "& .MuiChip-label": {
                      px: 0.5,
                    },
                  }}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("Select options")}
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    border: "1px solid #e1e5e9",
                    fontSize: "13px",
                    minHeight: "32px",
                    "& fieldset": { border: "none" },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "13px",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    fontSize: "13px",
                    color: theme.palette.text.secondary,
                  },
                }}
              />
            )}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                py: 0.75,
                px: 1.5,
              },
            }}
          />
        </FormControl>
      );
    } else {
      // Handle single-select filters (date filters)
      return (
        <FormControl sx={{ flex: 1 }}>
          <Select
            value={typeof filter.value === "string" ? filter.value : ""}
            onChange={(e) => onValueChange(filter.id, e.target.value)}
            displayEmpty
            size="small"
            MenuProps={{
              disablePortal: false,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                backgroundColor: "#f8f9fa",
                border: "1px solid #e1e5e9",
                fontSize: "13px",
                "& fieldset": { border: "none" },
              },
            }}
          >
            <MenuItem value="">
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "13px",
                }}
              >
                {t("Select option")}
              </Typography>
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Typography sx={{ fontSize: "13px" }}>
                  {option.label}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        {/* Filter Type Label */}
        <Box
          sx={{
            minWidth: 100,
            px: 2,
            py: 1,
            border: "1px solid #e1e5e9",
            borderRadius: "6px",
          }}
        >
          <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
            {getFilterTypeLabel(filter.type)}
          </Typography>
        </Box>

        {/* Filter Value Selector */}
        {renderFilterSelector()}

        {/* Delete Button */}
        <IconButton
          onClick={() => onRemove(filter.id)}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            padding: "6px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              color: theme.palette.error.main,
            },
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Date Range Picker */}
      {(filter.type === "due_date" ||
        filter.type === "start_date" ||
        filter.type === "updated_date") &&
        renderDateRangePicker()}
    </Box>
  );
};
